/** Bridges the scheduler to the vector worker pool. */

import type { HouseDetail } from '../core/build/houses.js';
import type { VectorChunkData } from '../core/build/vectorfeatures.js';
import type { GeoOrigin } from '../core/geo/enu.js';
import type { TileKey } from '../core/geo/tilekey.js';
import type { ElevationEncoding, VectorSource } from '../core/sources/types.js';
import { clampToSourceZoom, expandTileUrl } from '../core/sources/types.js';
import type { DesiredTile } from '../core/stream/lod.js';
import type { TileLoader } from '../core/stream/scheduler.js';
import { WorkerPool } from '../workers/pool.js';
import { coverageOf } from './elevationLoader.js';
import type { CoverageIndex } from './elevationLoader.js';
import type { BuildingTagService } from './buildingTagService.js';
import type { OsmLineService } from './osmLineService.js';
import type { VectorJob } from '../workers/protocol.js';

export interface VectorLoaderOptions {
  maxTrees: number;
  densityScale: number;
  roadMaxSegment: number;
  minRoadHalfWidth: number;
  houseDetail: HouseDetail;
  /** Day of year at request time. */
  getDay?: () => number;
  /** OSM building tags, when enabled. See `buildingTagService.ts`. */
  buildingTags?: BuildingTagService;
  /** OSM power lines, fences, walls, hedges and masts, when enabled. See `osmLineService.ts`. */
  osmLines?: OsmLineService;
  /** Sweden's forest tiles, from NMD. See `WorldConfig.forest`. */
  forest?: { urlTemplate: string; zoom: number; index: string; trees?: { urlTemplate: string; index: string } };
  /** The elevation source's overlay, the laser ground model: its tile under each vector tile goes to the worker too. See `VectorJob.elevation`. */
  elevation?: { overlay?: { urlTemplate: string; zoom: number; index?: string }; encoding: ElevationEncoding; tileSize: number };
}

export class VectorTileLoader implements TileLoader<VectorChunkData> {
  private readonly pool: WorkerPool<VectorJob, VectorChunkData>;

  constructor(
    private readonly source: VectorSource,
    private readonly getOrigin: () => GeoOrigin,
    private readonly options: VectorLoaderOptions,
    workers: number,
  ) {
    this.pool = new WorkerPool<VectorJob, VectorChunkData>(
      () =>
        new Worker(new URL('../workers/vector.worker.ts', import.meta.url), { type: 'module' }),
      workers,
    );
  }

  /** Which tiles have a forest tile, and which single trees, from their coverage lists; each fetched once. */
  private readonly coverage = new Map<string, Promise<{ has(key: string): boolean; hasTile(key: TileKey): boolean }>>();

  private covered(index: string | undefined): Promise<{ has(key: string): boolean; hasTile(key: TileKey): boolean }> {
    const none = { has: () => false, hasTile: () => false };
    if (index === undefined) return Promise.resolve(none);
    let list = this.coverage.get(index);
    if (list === undefined) {
      list = fetch(index)
        .then(r => (r.ok ? r.json() : {}) as Promise<CoverageIndex>)
        .then(coverageOf)
        // Without the list, OpenStreetMap's forest everywhere.
        .catch(() => none);
      this.coverage.set(index, list);
    }
    return list;
  }

  async load(tile: DesiredTile, signal: AbortSignal): Promise<VectorChunkData> {
    const key = clampToSourceZoom(tile.key, this.source.minZoom, this.source.maxZoom);
    // Sweden's own tiles where they are: they carry what Overpass is asked for elsewhere.
    const overlay = this.source.overlay;
    const ownTiles = await this.covered(overlay !== undefined && key.z === overlay.zoom ? overlay.index : undefined);
    const own = (k: { z: number; x: number; y: number }) => overlay !== undefined && ownTiles.has(`${k.z}/${k.x}/${k.y}`);
    const urlOf = (k: { z: number; x: number; y: number }) => expandTileUrl(own(k) ? overlay!.urlTemplate : this.source.urlTemplate, k);
    const swedenTile = own(key);
    // Waited for here, before the job is queued, so a worker is never held
    // idle on the network. Undefined — off, slow or down — builds without.
    const forest = this.options.forest;
    const laser = this.options.elevation;
    const [buildingTags, osmLines, forestTiles, treeTiles, laserTiles] = await Promise.all([
      swedenTile ? undefined : this.options.buildingTags?.forTile(key, signal),
      swedenTile ? undefined : this.options.osmLines?.forTile(key, signal),
      this.covered(forest?.index),
      this.covered(forest?.trees?.index),
      this.covered(laser?.overlay !== undefined && key.z === laser.overlay.zoom ? laser.overlay.index : undefined),
    ]);
    const id = `${key.z}/${key.x}/${key.y}`;
    const forestUrl = forest !== undefined && key.z === forest.zoom && forestTiles.has(id)
      ? expandTileUrl(forest.urlTemplate, key) : undefined;
    // Single trees only with the cells, which say what kind each is.
    const treesUrl = forestUrl !== undefined && forest?.trees !== undefined && treeTiles.has(id)
      ? expandTileUrl(forest.trees.urlTemplate, key) : undefined;
    const job: VectorJob = {
      key,
      url: urlOf(key),
      // Each from where it is built from, so the roads meet as that tile has them.
      neighbourUrls: {
        west: urlOf({ z: key.z, x: (key.x - 1 + 2 ** key.z) % 2 ** key.z, y: key.y }),
        north: key.y > 0 ? urlOf({ z: key.z, x: key.x, y: key.y - 1 }) : undefined,
      },
      swedenTile,
      origin: this.getOrigin(),
      maxTrees: this.options.maxTrees,
      densityScale: this.options.densityScale,
      roadMaxSegment: this.options.roadMaxSegment,
      minRoadHalfWidth: this.options.minRoadHalfWidth,
      houseDetail: this.options.houseDetail,
      dayOfYear: this.options.getDay?.() ?? 172,
      buildingTags,
      osmLines,
      forestUrl,
      treesUrl,
      elevation: laser?.overlay !== undefined && laserTiles.hasTile(key)
        ? { url: expandTileUrl(laser.overlay.urlTemplate, key), encoding: laser.encoding, tileSize: laser.tileSize } : undefined,
    };
    return this.pool.run(job, [], signal);
  }

  dispose(): void {
    this.pool.dispose();
  }
}
